import React from "react";
import { Link, useParams } from "react-router-dom";
import {
  CommonBanner,
  CourseTimetable,
  Footer,
  Header,
} from "../../components";
import {
  STAGES,
  findProgram,
  findYear,
  labelFor,
  linkFor,
  spellOut,
  subjectFor,
  whenText,
} from "../../constants/catalogue";
import YearPlan from "../../components/Common/year-plan/YearPlan";
import {
  SENIOR_FAQS,
  SHARED_FAQS,
  stepsForStage,
  studyFor,
  contentFor,
} from "../../constants/programContent";
import { C, FONT, container } from "../../constants/mq";

// One page template for every program, at every year level.
//
// URL: /courses/year-5/selective-preliminary-trial-tests
//
// Only "What's covered" needs writing per program (programContent.js).
// The rest — year levels, term structure, related courses — is generated,
// and any section without real content is left out rather than filled with
// placeholder text.

// Macquarie look (src/constants/mq.js): only the banner is red. Sections
// alternate white and sand, cards are white with a hairline border, and the
// closing call to action is a sand box with one red button.
const s = {
  section: { background: "#ffffff", padding: "56px 0", fontFamily: FONT },
  sectionAlt: { background: C.sand, padding: "56px 0", fontFamily: FONT },
  container: { ...container },
  // Reading column, left-aligned under the banner title.
  narrow: { maxWidth: 800 },
  lead: {
    fontSize: 18,
    lineHeight: 1.75,
    color: C.body,
    fontWeight: 500,
    margin: 0,
  },
  h2: {
    fontSize: 28,
    fontWeight: 700,
    color: C.ink,
    margin: "0 0 10px",
    letterSpacing: "-0.3px",
    lineHeight: 1.25,
  },
  h2sub: { fontSize: 15, color: C.muted, margin: "0 0 28px", lineHeight: 1.65 },

  glance: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: 1,
    background: C.line,
    border: `1px solid ${C.line}`,
    borderRadius: 4,
    overflow: "hidden",
  },
  glanceCell: { background: "#fff", padding: "18px 20px" },
  glanceLabel: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "1.2px",
    textTransform: "uppercase",
    color: C.muted,
    margin: "0 0 6px",
  },
  glanceValue: { fontSize: 15, fontWeight: 700, color: C.ink, margin: 0 },
  glanceNote: { fontSize: 12.5, lineHeight: 1.45, color: C.muted, margin: "4px 0 0" },

  // Other year levels as underlined tabs (.mq-tabs / .mq-tab).
  yearRow: { marginTop: 24, flexWrap: "wrap", rowGap: 0 },
  yearTab: { textDecoration: "none" },

  steps: { display: "flex", flexDirection: "column", gap: 12 },
  step: {
    display: "grid",
    gridTemplateColumns: "36px 1fr",
    gap: 16,
    alignItems: "start",
    padding: "18px 20px",
  },
  stepNo: {
    width: 28,
    height: 28,
    borderRadius: 999,
    background: C.bright,
    color: "#fff",
    fontSize: 13,
    fontWeight: 700,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  stepName: { fontSize: 15.5, fontWeight: 700, color: C.ink, margin: "0 0 4px" },
  stepBody: { fontSize: 14, lineHeight: 1.65, color: C.body, margin: 0 },

  studyGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
    gap: 20,
  },
  studyCard: { padding: "22px 20px 24px" },
  studyTitle: {
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: "1.2px",
    textTransform: "uppercase",
    color: C.red,
    margin: "0 0 10px",
  },
  studyBody: { fontSize: 14, lineHeight: 1.7, color: C.body, margin: 0 },
  moduleRow: { borderTop: `1px solid ${C.line}`, padding: "18px 0" },
  moduleTitle: { fontSize: 16, fontWeight: 700, color: C.ink, margin: "0 0 8px" },
  moduleList: { margin: 0, paddingLeft: 20 },
  moduleItem: { fontSize: 14.5, lineHeight: 1.75, color: C.body },

  pending: {
    padding: "22px 24px",
    fontSize: 14,
    lineHeight: 1.7,
    color: C.body,
  },

  faq: { borderTop: `1px solid ${C.line}`, padding: "18px 0" },
  q: { fontSize: 15, fontWeight: 700, color: C.ink, margin: "0 0 6px" },
  a: { fontSize: 14, lineHeight: 1.7, color: C.body, margin: 0 },

  related: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(230px, 1fr))",
    gap: 16,
  },
  relCard: { display: "block", padding: "16px 18px 18px" },
  relName: { fontSize: 14.5, fontWeight: 700, color: C.ink, margin: "0 0 3px" },
  relMeta: { fontSize: 12.5, color: C.muted, margin: 0 },

  // Closing call to action: sand box, ink heading, one red button
  // (same pattern as FreeAssessment).
  ctaBand: { background: "#ffffff", padding: "56px 0 72px", fontFamily: FONT },
  ctaCard: {
    padding: "26px 26px",
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 20,
  },
  ctaTitle: { fontSize: 19, fontWeight: 700, color: C.ink, margin: "0 0 4px", lineHeight: 1.4 },
  ctaBody: { fontSize: 14, color: C.muted, margin: 0, lineHeight: 1.5 },
  ctaRow: { display: "flex", flexWrap: "wrap", alignItems: "center", gap: 24 },
};

// Full-width content column with a narrower reading measure inside it, so
// the text lines up with the banner title rather than floating centred.
const Column = ({ children }) => (
  <div style={s.container}>
    <div style={s.narrow}>{children}</div>
  </div>
);

const NotFound = () => (
  <div style={{ background: "#fff" }}>
    <Header />
    <CommonBanner
      bannerHeading="Course not found"
      subheading="That course is not one we run. Browse everything we teach instead."
      path={[{ link: "/our-courses", name: "Courses" }]}
    />
    <section style={s.section}>
      <Column>
        <Link className="mq-btn" to="/our-courses">
          Browse all courses
        </Link>
      </Column>
    </section>
    <Footer />
  </div>
);

const CoursePage = () => {
  const { yearSlug, programSlug } = useParams();
  const year = Number(String(yearSlug || "").replace("year-", ""));
  const program = findProgram(programSlug);
  const yearEntry = findYear(year);

  // Guard against a URL that names a real program but a year it is not run in.
  if (!program || !yearEntry || !program.years.includes(year)) return <NotFound />;

  const content = contentFor(program.slug);
  const stage = STAGES[yearEntry.stage];

  // Other subjects taught at this year level.
  const sameYear = yearEntry.subjects.filter((sub) => sub.slug !== program.slug);
  // The same program at other year levels.
  const otherYears = program.years.filter((y) => y !== year);

  const isSenior = yearEntry.stage === "senior";
  // This year's entry for the course: when it runs (Year 6) and, for High
  // School Preparation, the high school subjects it is made of.
  const entry = subjectFor(year, program.slug);
  const parts = entry?.parts || [];
  // High School Preparation is taught as high school classes, so it follows
  // the high school seven steps even though Year 6 is primary.
  const steps = stepsForStage(parts.length ? "high" : yearEntry.stage);
  // The comprehensive primary course is named differently at each year level.
  const title = labelFor(year, program.slug) || program.name;
  // What that name stands for (WRMT, WEM), for families who don't know it.
  const spelled = spellOut(title);
  // Per-year subject breakdown, for the comprehensive course only.
  // WEM is the same Year 6 work without Thinking Skills, and calls the
  // reading strand English.
  const study =
    program.slug === "wrmt"
      ? studyFor(year)
      : program.slug === "wem"
        ? (studyFor(year) || [])
            .filter((x) => x.title !== "Thinking Skills")
            .map((x) => (x.title === "Reading Skills" ? { ...x, title: "English" } : x))
        : null;
  const faqs = [
    ...(content.faqs || []),
    ...(isSenior ? SENIOR_FAQS : []),
    ...SHARED_FAQS,
  ];

  return (
    <div style={{ background: "#fff" }}>
      <Header />
      <CommonBanner
        bannerHeading={`Year ${year} ${title}`}
        subheading={
          spelled
            ? `Our comprehensive primary program: ${spelled} in one course.`
            : content.tagline || undefined
        }
        path={[
          { link: "/our-courses", name: "Courses" },
          { link: `/courses/year-${year}/${program.slug}`, name: title },
        ]}
      />

      <main>
        {/* At a glance — generated */}
        <section style={s.section}>
          <div style={s.container}>
            <div style={s.glance}>
              <div style={s.glanceCell}>
                <p style={s.glanceLabel}>Year level</p>
                <p style={s.glanceValue}>Year {year}</p>
              </div>
              <div style={s.glanceCell}>
                <p style={s.glanceLabel}>Stage</p>
                <p style={s.glanceValue}>{stage}</p>
              </div>
              <div style={s.glanceCell}>
                <p style={s.glanceLabel}>Program</p>
                <p style={s.glanceValue}>{title}</p>
                {spelled && <p style={s.glanceNote}>{spelled}</p>}
              </div>
              {entry?.when?.length > 0 && (
                <div style={s.glanceCell}>
                  <p style={s.glanceLabel}>When</p>
                  <p style={s.glanceValue}>{whenText(entry.when)}</p>
                </div>
              )}
              <div style={s.glanceCell}>
                <p style={s.glanceLabel}>Also offered in</p>
                <p style={s.glanceValue}>
                  {otherYears.length
                    ? otherYears.map((y) => `Year ${y}`).join(", ")
                    : "This year level only"}
                </p>
              </div>
            </div>

            {entry?.when?.length > 0 && (
              <div style={{ marginTop: 28 }}>
                <YearPlan year={year} />
              </div>
            )}

            {otherYears.length > 0 && (
              <div className="mq-tabs" style={s.yearRow}>
                {program.years.map((y) => (
                  <Link
                    key={y}
                    to={`/courses/year-${y}/${program.slug}`}
                    className={`mq-tab${y === year ? " mq-tab--on" : ""}`}
                    style={s.yearTab}
                  >
                    Year {y}
                  </Link>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Overview — written */}
        {content.intro && (
          <section style={{ ...s.section, paddingTop: 0 }}>
            <Column>
              <p style={s.lead}>{content.intro}</p>
            </Column>
          </section>
        )}

        {/* What's covered — written, omitted until we have the curriculum */}
        <section style={s.sectionAlt}>
          <Column>
            <h2 style={s.h2}>
              {study ? "What students will study" : "What\u2019s covered"}
            </h2>
            {study ? (
              <>
                <p style={s.h2sub}>
                  {study.length === 3 ? "Three" : "Four"} subjects in one course, taught to the Year {year}{" "}
                  standard.
                </p>
                <div style={s.studyGrid}>
                  {study.map((sub) => (
                    <div key={sub.title} className="mq-card" style={s.studyCard}>
                      <h3 style={s.studyTitle}>{sub.title}</h3>
                      <p style={s.studyBody}>{sub.body}</p>
                    </div>
                  ))}
                </div>
              </>
            ) : parts.length ? (
              <>
                <p style={s.h2sub}>
                  The three high school subjects, taught as our Y{year}/{year + 1} classes in{" "}
                  {whenText(entry.when)} at the high school campuses.
                </p>
                <div style={s.studyGrid}>
                  {parts.map((part) => (
                    <div key={part.slug} className="mq-card" style={s.studyCard}>
                      <h3 style={s.studyTitle}>{part.name}</h3>
                      <p style={s.studyBody}>
                        Y{year}/{year + 1} {part.name} &middot; {whenText(entry.when)}
                      </p>
                      <p style={{ ...s.studyBody, marginTop: 10 }}>
                        <Link
                          to={`/courses/year-${year + 1}/${part.slug}`}
                          style={{ color: C.red, fontWeight: 600 }}
                        >
                          Continues in Year {year + 1} &rsaquo;
                        </Link>
                      </p>
                    </div>
                  ))}
                </div>
              </>
            ) : content.covered?.modules?.length ? (
              <>
                {content.covered.note && (
                  <p style={s.h2sub}>{content.covered.note}</p>
                )}
                {content.covered.modules.map((m) => (
                  <div key={m.title} style={s.moduleRow}>
                    <h3 style={s.moduleTitle}>{m.title}</h3>
                    <ul style={s.moduleList}>
                      {m.items.map((it) => (
                        <li key={it} style={s.moduleItem}>
                          {it}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </>
            ) : (
              <>
                <p style={s.h2sub}>
                  The full outline for this course is available from our team.
                </p>
                <div className="mq-card" style={s.pending}>
                  We would rather send you the actual course outline than
                  summarise it here. Ask us for the {program.name} outline for
                  Year {year} and we will email it through, along with the
                  current timetable.
                </div>
              </>
            )}
          </Column>
        </section>

        {/* How a term works — real LMS stages, only for what we deliver here */}
        {!program.external && !isSenior && (
          <section style={s.section}>
            <Column>
              <h2 style={s.h2}>How a term works</h2>
              <p style={s.h2sub}>
                Every student moves through the same seven steps each term, and
                you can see where they are at any point from the parent login.
              </p>
              <div style={s.steps}>
                {steps.map((st, i) => (
                  <div key={st.name} className="mq-card" style={s.step}>
                    <span style={s.stepNo}>{i + 1}</span>
                    <div>
                      <p style={s.stepName}>{st.name}</p>
                      <p style={s.stepBody}>{st.body}</p>
                    </div>
                  </div>
                ))}
              </div>
            </Column>
          </section>
        )}

        {/* Timetable — placeholder until the PDFs are loaded in */}
        <section style={program.external || isSenior ? s.section : s.sectionAlt}>
          <Column>
            <h2 style={s.h2}>Timetable</h2>
            <p style={s.h2sub}>
              Class times at each campus. Every campus sets its own.
            </p>
            {parts.length ? (
              // One timetable per subject: they are separate classes.
              parts.map((part) => (
                <div key={part.slug} style={{ marginBottom: 28 }}>
                  <h3 style={{ ...s.studyTitle, marginBottom: 10 }}>
                    Y{year}/{year + 1} {part.name}
                  </h3>
                  <CourseTimetable
                    slug={part.slug}
                    year={year}
                    courseName={`Y${year}/${year + 1} ${part.name}`}
                  />
                </div>
              ))
            ) : (
              <CourseTimetable
                slug={program.slug}
                year={year}
                courseName={title}
              />
            )}
          </Column>
        </section>

        {/* Related — generated */}
        {(sameYear.length > 0 || otherYears.length > 0) && (
          <section style={s.section}>
            <Column>
              <h2 style={s.h2}>Related courses</h2>
              <p style={s.h2sub}>
                {sameYear.some((sub) => sub.when?.length)
                  ? `The other Year ${year} courses, and when in the year each one runs.`
                  : "Other programs your child could take alongside this one."}
              </p>
              <div style={s.related}>
                {sameYear.map((sub) => (
                  <Link
                    key={sub.slug}
                    to={linkFor(year, sub.name).href}
                    className="mq-card"
                    style={s.relCard}
                  >
                    <p style={s.relName}>{sub.label || sub.name}</p>
                    <p style={s.relMeta}>
                      Year {year}
                      {sub.when?.length ? ` · ${whenText(sub.when)}` : ""}
                    </p>
                  </Link>
                ))}
                {otherYears.map((y) => (
                  <Link
                    key={`${program.slug}-${y}`}
                    to={`/courses/year-${y}/${program.slug}`}
                    className="mq-card"
                    style={s.relCard}
                  >
                    <p style={s.relName}>{program.name}</p>
                    <p style={s.relMeta}>Year {y}</p>
                  </Link>
                ))}
              </div>
            </Column>
          </section>
        )}

        {/* FAQ */}
        <section style={s.sectionAlt}>
          <Column>
            <h2 style={s.h2}>Frequently asked questions</h2>
            <div style={{ marginTop: 20 }}>
              {faqs.map((f) => (
                <div key={f.q} style={s.faq}>
                  <p style={s.q}>{f.q}</p>
                  <p style={s.a}>{f.a}</p>
                </div>
              ))}
            </div>
          </Column>
        </section>

        <section style={s.ctaBand}>
          <div style={s.container}>
            <div className="mq-sand" style={s.ctaCard}>
              <div>
                <p style={s.ctaTitle}>
                  Ready to start Year {year} {title}?
                </p>
                <p style={s.ctaBody}>
                  Enrol now, or ask us for the outline and timetable first.
                </p>
              </div>
              <div style={s.ctaRow}>
                <Link className="mq-btn" to="/enroll">
                  Enrol now
                </Link>
                <Link className="mq-link" to="/contact-us">
                  Ask a question
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default CoursePage;
